import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, ShieldCheck, Users, Building2, Activity, Search } from "lucide-react";
import { Button } from "../components/ui/button";

const heroImg = "https://images.unsplash.com/photo-1771343917024-0b5397850ccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHx2ZXRlcmFuJTIwY29tbXVuaXR5JTIwc3VwcG9ydCUyMHNtaWxpbmd8ZW58MHx8fHwxNzgxNjk1MjcxfDA&ixlib=rb-4.1.0&q=85";
const housingImg = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwZGF5fGVufDB8fHx8MTc4MTY5NTI3Mnww&ixlib=rb-4.1.0&q=85";
const cmImg = "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzb2NpYWwlMjB3b3JrZXIlMjB0YWxraW5nJTIwd2l0aCUyMGNsaWVudHxlbnwwfHx8fDE3ODE2OTUyNzF8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* Hero — asymmetric editorial */}
      <section className="border-b border-[#D1D5DB] shv-grain">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-[#C05621] shv-live" /> Real-time housing availability
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-[#0A192F] leading-[1.05]">
              Empty beds.<br />
              <span className="text-[#1E3A8A]">Full purpose.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-xl leading-relaxed">
              Safe Harbor Veterans is a housing placement and referral network — connecting verified housing providers, case managers, and seekers (veterans, seniors, people in recovery, returning citizens, families in crisis, and anyone facing housing instability) through real-time vacancies, verified applicants, and tracked placements.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/search" data-testid="hero-find-housing">
                <Button className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white px-7 py-6 rounded-md text-base">
                  Get Connected <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register?role=provider" data-testid="hero-list-beds">
                <Button variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-7 py-6 rounded-md text-base">
                  List Your Beds
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex gap-8 pt-8 border-t border-[#D1D5DB]">
              <Stat label="Populations Served" value="10+" />
              <Stat label="Launch Market" value="North Carolina" />
              <Stat label="Seekers Pay" value="$0" accent />
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <img src={heroImg} alt="Veteran community" className="w-full h-[480px] object-cover rounded-sm border border-[#D1D5DB]" />
              <div className="absolute -bottom-4 -left-4 bg-white border border-[#D1D5DB] p-4 max-w-[240px] shadow-md">
                <div className="text-xs uppercase tracking-widest text-[#C05621] font-semibold">Live availability</div>
                <div className="font-heading text-2xl font-bold text-[#0A192F] mt-1">3 beds</div>
                <div className="text-xs text-[#4A5568]">Recovery housing · Charlotte, NC</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three-role grid */}
      <section className="border-b border-[#D1D5DB] bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
          <div className="max-w-2xl mb-16">
            <div className="shv-rule mb-4"></div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Built for everyone in the placement chain</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D1D5DB]">
            <RoleCard
              tone="primary"
              icon={<Search className="w-5 h-5" />}
              kicker="Housing Seekers"
              title="Free, always."
              body="Search beds, transitional housing, recovery residences, ILFs, and shelters. Submit one intake — get matched to verified providers."
              cta="Start Search"
              to="/search"
            />
            <RoleCard
              icon={<Building2 className="w-5 h-5" />}
              kicker="Housing Providers"
              title="Fill beds faster."
              body="Empty beds cost you money. Post real-time vacancies, receive vetted referrals, and unlock placement services that actually move people in."
              cta="List Beds"
              to="/register?role=provider"
            />
            <RoleCard
              icon={<Users className="w-5 h-5" />}
              kicker="Case Managers & Agencies"
              title="Stop calling. Start placing."
              body="One dashboard. Save clients, broadcast referrals, track outcomes. Agency accounts roll up the entire team."
              cta="Open Pro Account"
              to="/register?role=case_manager"
            />
          </div>
        </div>
      </section>

      {/* The marketplace explainer */}
      <section className="bg-[#F5F5F0] border-b border-[#D1D5DB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <img src={housingImg} alt="Housing" className="w-full rounded-sm border border-[#D1D5DB]" />
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">The Model</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] leading-tight">
              Not a website. A <span className="text-[#1E3A8A]">referral engine</span>.
            </h2>
            <p className="mt-4 text-[#4A5568] text-lg leading-relaxed">
              Traffic doesn't fill beds — qualified, verified applicants do. Safe Harbor combines a real-time vacancy map with intake verification, applicant matching, and a placement workflow that case managers and providers actually use.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <Feature icon={<BedDouble />} title="Real-Time Vacancies" body="Providers update available beds the moment they open." />
              <Feature icon={<ShieldCheck />} title="Verified Providers" body="Admin-verified badges so seekers and case managers know who to trust." />
              <Feature icon={<Activity />} title="Vacancy Alerts" body="Push openings to matched seekers and case managers instantly." />
              <Feature icon={<Users />} title="Placement Workflow" body="Referrals move from sent → accepted → placed with full history." />
            </div>
          </div>
        </div>
      </section>

      {/* Case manager band */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">For Agencies & Case Managers</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight">Stop spending hours making calls.</h2>
            <p className="mt-4 text-white/70 text-lg max-w-xl">
              Built for VA contractors, reentry agencies, recovery programs, and social service teams. One agency login, the whole roster, real outcomes.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm text-white/80">
              <li>· Save clients & track every referral</li>
              <li>· Real-time vacancy push notifications</li>
              <li>· Verified Applicant packages</li>
              <li>· Agency-wide reporting</li>
            </ul>
            <Link to="/register?role=case_manager" data-testid="cta-agency" className="inline-block mt-10">
              <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white px-7 py-6 rounded-md">Open Pro Account <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
          <div className="lg:col-span-5">
            <img src={cmImg} alt="Case manager" className="w-full h-[400px] object-cover rounded-sm" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white border-b border-[#D1D5DB]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-24 text-center">
          <div className="shv-rule mx-auto mb-6"></div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] tracking-tight">
            Every empty bed is a person waiting.
          </h2>
          <p className="mt-5 text-lg text-[#4A5568] max-w-2xl mx-auto">
            Join Safe Harbor Veterans and become part of the housing network for those who served, returned, or are starting over.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/register" data-testid="cta-register"><Button className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white px-7 py-6">Create Free Account</Button></Link>
            <Link to="/search" data-testid="cta-browse"><Button variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-7 py-6">Browse Housing</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className={`font-heading font-bold text-2xl ${accent ? "text-[#C05621]" : "text-[#0A192F]"}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#4A5568] mt-1">{label}</div>
  </div>
);

const RoleCard = ({ icon, kicker, title, body, cta, to, tone }) => (
  <div className={`p-8 bg-white ${tone === "primary" ? "lg:border-l-4 lg:border-l-[#C05621]" : ""}`}>
    <div className="w-10 h-10 rounded-sm bg-[#F5F5F0] border border-[#D1D5DB] flex items-center justify-center text-[#1E3A8A] mb-5">{icon}</div>
    <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">{kicker}</div>
    <h3 className="font-heading text-2xl font-bold text-[#0A192F] mb-3">{title}</h3>
    <p className="text-sm text-[#4A5568] leading-relaxed mb-6">{body}</p>
    <Link to={to} className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:text-[#C05621] transition-colors">
      {cta} <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

const Feature = ({ icon, title, body }) => (
  <div>
    <div className="w-8 h-8 rounded-sm bg-white border border-[#D1D5DB] flex items-center justify-center text-[#1E3A8A] mb-2">
      {React.cloneElement(icon, { className: "w-4 h-4" })}
    </div>
    <div className="font-heading font-semibold text-[#0A192F]">{title}</div>
    <div className="text-sm text-[#4A5568] mt-1">{body}</div>
  </div>
);
